'use strict';

/**
 * Bootstrap Select — Initialization & WCAG 2.2 AAA Accessibility
 * Compatible with bootstrap-select v1.13.18
 *
 * Strategy: minimal, non-invasive intervention — never modify DOM attributes
 * that bootstrap-select uses for its own internal state tracking.
 *
 * What bootstrap-select already provides (line references in bootstrap-select.js):
 *   - button[role="combobox"][aria-haspopup="listbox"][aria-expanded][aria-owns]  (L1124)
 *   - div.inner[role="listbox"][id="bs-select-N"][tabindex="-1"]                  (L1142)
 *   - ul.inner[role="presentation"]                                               (L1143)
 *   - tabindex="-1" on disabled <a>, tabindex="0" on enabled <a>                 (L2366-2369)
 *   - .selected / .active classes managed internally
 *
 * What we add:
 *   1. aria-selected on <a role="option"> elements (bootstrap-select omits this)
 *   2. aria-hidden + tabindex="-1" on <a> of hidden <li> (ghost Tab stop fix)
 *   3. aria-labelledby on button pointing to label + value span (name + current value)
 *   4. aria-controls on button (mirrors aria-owns, per ARIA 1.2 combobox pattern)
 *   5. aria-required / aria-disabled mirrored from native <select>
 *   6. Live region announcing selection changes to screen readers
 *   7. Space/Enter on closed button: stopPropagation to prevent bootstrap-select's
 *      delegated keydown from eating preventDefault before the native click fires
 *   8. Tab inside open dropdown: close + return focus to button
 *
 * What we do NOT touch:
 *   - The ID on div.inner (bootstrap-select sets it; aria-owns references it)
 *   - role on <li> elements (tracked by bootstrap-select via activeIndex / canHighlight)
 *   - IDs on <a> elements (bootstrap-select assigns id="bs-select-N-M"; we
 *     read but never override them — used for aria-activedescendant)
 *   - tabindex on visible, enabled <a> (bootstrap-select manages this)
 *
 * Empty-value option:
 *   data-hidden="true" is set on the <option> — the only attribute buildData
 *   respects (line 1500: `:not([hidden]):not([data-hidden="true"])`).
 *   jQuery .hide() sets CSS display:none which buildData ignores.
 */

const SELECTOR = 'select.selectpicker';
const PATCHED_ATTR = 'data-a11y-patched';
const LIVE_REGION_ID = 'js-select-live-region';

// ─── Live region ──────────────────────────────────────────────────────────────

function ensureLiveRegion() {
    if (!$(`#${LIVE_REGION_ID}`).length) {
        $('body').append(
            `<div id="${LIVE_REGION_ID}" class="cc-visibility-hidden" role="status" aria-live="polite" aria-atomic="true"></div>`
        );
    }
}

function announce(text) {
    if (!text) return;
    const $region = $(`#${LIVE_REGION_ID}`);
    $region.text('');
    setTimeout(() => $region.text(text), 50);
}

// ─── Option handling ──────────────────────────────────────────────────────────

/**
 * Hide empty-value options from bootstrap-select's rendered list while
 * keeping the button label correct when that option is selected.
 *
 * buildData filters with :not([hidden]):not([data-hidden="true"]) (line 1500)
 * so data-hidden="true" removes the option from the dropdown list entirely.
 * But if it's selected, bootstrap-select falls back to noneSelectedText
 * ("Nothing selected") because selectedOptions is empty.
 *
 * Fix: copy the option's text into the selectpicker `title` option before
 * calling refresh. bootstrap-select shows `title` in the button when nothing
 * is selected (line 1807), so the button reads correctly. The option is then
 * hidden from the list with data-hidden="true".
 */
function hideEmptyValueOptions($select) {
    $select.find('option[value=""]').each(function () {
        const $opt = $(this);
        const text = $opt.text().trim();
        if (text) {
            $select.attr('title', text);
        }
        $opt.attr('data-hidden', 'true');
    });
}

/**
 * Sync only the ARIA attributes that bootstrap-select does not manage:
 * - aria-selected on visible option <a> elements
 * - aria-hidden + tabindex="-1" on <a> of hidden <li>
 *
 * We do NOT touch: role on <li>, id on <a>, tabindex on visible <a>,
 * or any attribute used by bootstrap-select's activeIndex / canHighlight logic.
 */
function syncOptions($listbox) {
    $listbox.find('li').each(function () {
        const $li = $(this);
        const $a = $li.find('a').first();
        if (!$a.length) return;

        if ($li.hasClass('hidden')) {
            // The <li> is hidden by bootstrap-select. Its <a> still has
            // tabindex="0" (set by bootstrap-select since the option is not
            // disabled). This creates a ghost Tab stop. Override it.
            $li.attr('aria-hidden', 'true');
            $a.attr('tabindex', '-1').removeAttr('aria-selected');
        } else {
            $li.removeAttr('aria-hidden');
            // aria-selected is required for role="option" (ARIA 4.1.2).
            // bootstrap-select does not set this attribute.
            $a.attr('aria-selected', $li.hasClass('selected') ? 'true' : 'false');
        }
    });
}

// ─── Core patch ───────────────────────────────────────────────────────────────

function patchSelect($select, index) {
    const $wrapper = $select.closest('.bootstrap-select');
    if (!$wrapper.length || $wrapper.attr(PATCHED_ATTR) === 'true') return;

    const $btn = $wrapper.find('button.dropdown-toggle');
    const $dropdownMenu = $wrapper.find('.dropdown-menu');
    // .children('.inner') — only the direct-child div.inner (the scrollable
    // container). .find('.inner') would also match ul.dropdown-menu.inner,
    // causing bootstrap-select's delegated keydown handler to crash (the <ul>
    // has class "dropdown-menu", so $parent.data('this') would be undefined).
    const $listbox = $dropdownMenu.children('.inner');

    if (!$btn.length || !$listbox.length) return;

    // Use bootstrap-select's own ID — do NOT override it.
    // The button has aria-owns pointing to this ID; $menu.data('this') is keyed
    // to this element. Overriding the ID breaks both references.
    const listboxId = $listbox.attr('id') || '';

    const selectId = $select.attr('id') || '';

    // ── Accessible name ────────────────────────────────────────────────────────
    // aria-labelledby="label-id value-span-id" announces "Label: CurrentValue".
    // aria-label alone would suppress the selected value from screen readers.
    const btnId = `bs-select-btn-${index}`;
    const $label = selectId ? $(`label[for="${selectId}"]`) : $();
    let labelText = '';

    if ($label.length) {
        labelText = $label.text().trim();
        if (!$label.attr('id')) {
            $label.attr('id', `bs-select-lbl-${index}`);
        }
        // The label's for="<selectId>" points to the aria-hidden native select,
        // which triggers "label references hidden element" (WCAG 4.1.2).
        // Re-point for to the button ID so AT correctly associates label and control.
        // Clicking the label natively focuses the button — no extra handler needed.
        $label.attr('for', btnId).off('click.bs-a11y');
    }

    const labelElId = $label.length ? $label.attr('id') : null;
    const valueSpanId = `bs-select-val-${index}`;
    $btn.attr('id', btnId);
    $btn.find('.filter-option-inner-inner').attr('id', valueSpanId);

    // ── Native select ─────────────────────────────────────────────────────────
    // bootstrap-select already adds class "bs-select-hidden" (display:none).
    // Add aria-hidden so AT skips the native element entirely, and tabindex="-1"
    // to ensure it is not reachable via keyboard (bootstrap-select copies the
    // original tabindex as the string "null" which browsers may treat as 0).
    $select.attr({ 'aria-hidden': 'true', tabindex: '-1' });

    // ── Button ────────────────────────────────────────────────────────────────
    // bootstrap-select sets: role="combobox", aria-haspopup="listbox",
    // aria-expanded, aria-owns, tabindex="-1" (then overrides on focus).
    // We add: aria-controls (ARIA 1.2 prefers this over aria-owns),
    //         aria-labelledby, aria-required, aria-disabled.
    // We override tabindex to "0" so it's reachable without focus event.
    $btn.removeAttr('title data-original-title');
    if (listboxId) $btn.attr('aria-controls', listboxId);
    $btn.attr('tabindex', '0');

    if (labelElId) {
        $btn.attr('aria-labelledby', `${labelElId} ${valueSpanId}`);
    } else if (labelText) {
        $btn.attr('aria-label', labelText);
    }

    if ($select.prop('required')) $btn.attr('aria-required', 'true');
    if ($select.prop('disabled')) $btn.attr({ 'aria-disabled': 'true', tabindex: '-1' });

    // ── Listbox ────────────────────────────────────────────────────────────────
    // bootstrap-select already sets role="listbox" and the correct id.
    // We only add aria-label for extra context if a label exists.
    if (labelText) $listbox.attr('aria-label', labelText);

    syncOptions($listbox);

    // ── MutationObserver: prevent bootstrap-select from resetting tabindex/title─
    // bootstrap-select's focus/blur handlers reset tabindex to the original
    // <select> value (lines 2632-2642). Our MutationObserver restores tabindex="0".
    const btnObserver = new MutationObserver(() => {
        $btn.removeAttr('title data-original-title');
        if (!$select.prop('disabled') && $btn.attr('tabindex') !== '0') {
            $btn.attr('tabindex', '0');
        }
    });
    btnObserver.observe($btn[0], {
        attributes: true,
        attributeFilter: ['title', 'data-original-title', 'tabindex']
    });

    // ── Bootstrap-select events ────────────────────────────────────────────────
    $select
        .on('show.bs.select.a11y', () => $btn.attr('aria-expanded', 'true'))
        .on('hide.bs.select.a11y', () => {
            $btn.attr('aria-expanded', 'false');
            $listbox.removeAttr('aria-activedescendant');
        })
        // bootstrap-select fires 'refreshed.bs.select' (not 'rendered')
        .on('refreshed.bs.select.a11y', () => syncOptions($listbox))
        .on('changed.bs.select.a11y', function () {
            syncOptions($listbox);
            const selectedText = $(this).find('option:selected').text().trim();
            const announcement = labelText ? `${labelText}: ${selectedText}` : selectedText;
            announce(announcement);
        });

    // ── Button keydown: Space / Enter on closed dropdown ─────────────────────
    // bootstrap-select's keydown is delegated on document (line 3231). When
    // isActive=false, Space (32) and Enter (13) fall into the type-ahead block
    // and call e.preventDefault() (line 2952), blocking the native button click.
    // We intercept these two keys, call stopPropagation() to prevent the
    // document-delegated handler from running, and let the browser fire the
    // native click → Bootstrap 4 dropdown opens normally.
    // Guard: only when closed. When open, bootstrap-select correctly handles
    // Enter/Space for option selection (lines 3021-3043).
    $btn.on('keydown.bs-a11y', function (e) {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        if ($wrapper.hasClass('show')) return;
        e.stopPropagation();
    });

    // ── Arrow keys: aria-activedescendant tracks the active option ───────────────
    // bootstrap-select keeps DOM focus on div.inner[role="listbox"] and moves
    // .active on the <li> during arrow key navigation. Moving DOM focus to the
    // <a> breaks bootstrap-select: its delegated keydown handler targets
    // [role="listbox"], so subsequent arrow keys stop working.
    //
    // Correct ARIA composite widget pattern: focus stays on the listbox container;
    // aria-activedescendant references the ID of the currently active option.
    // bootstrap-select already assigns unique IDs to each <a role="option">
    // (e.g. "bs-select-1-0"), so we just read and reference them.
    //
    // MutationObserver fires as a microtask after bootstrap-select's full
    // synchronous keydown chain (including div.inner.focus()) completes.
    const activeObserver = new MutationObserver((mutations) => {
        if (!$wrapper.hasClass('show')) return;
        for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const $li = $(mutation.target);
                if ($li.is('li') && $li.hasClass('active') && !$li.hasClass('hidden') && !$li.attr('aria-hidden')) {
                    const activeId = $li.find('a').first().attr('id');
                    if (activeId) $listbox.attr('aria-activedescendant', activeId);
                    return;
                }
            }
        }
    });
    activeObserver.observe($listbox[0], {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
    });

    // ── Tab / Enter on focused <a> ─────────────────────────────────────────────
    // bootstrap-select's delegated keydown on document fires even when an <a>
    // has focus, because div.inner[role="listbox"] is an ancestor and jQuery
    // delegation matches ancestor elements in the bubble path.
    //
    // Tab: close dropdown and return focus to button.
    //   bootstrap-select treats Tab as ArrowDown (downOnTab, line 2839-2840).
    //   stopImmediatePropagation prevents the document handler from running.
    //
    // Enter: when DOM focus is on an <a> (Tab navigation), bootstrap-select's
    //   Enter handler would select the .active <li>, NOT the focused <a>.
    //   We intercept Enter, stop propagation so bootstrap-select never runs,
    //   and fire click() directly on the focused <a> so it gets selected.
    $listbox.on('keydown.bs-a11y', 'a', function (e) {
        if (e.key === 'Tab') {
            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();
            $btn.trigger('click.bs.dropdown.data-api').trigger('focus');
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();
            $(this).trigger('click');
        }
    });

    // ── focusin on <a>: keep .active in sync with Tab navigation ─────────────
    // When Tab moves DOM focus to an <a>, the visual highlight (.active on <li>)
    // may point to a different option. Sync .active to the focused <li> so
    // the aria-activedescendant MutationObserver, the visual highlight, and
    // the Enter handler (bootstrap-select fallback) all agree on the same option.
    $listbox.on('focusin.bs-a11y', 'a', function () {
        const $a = $(this);
        const $li = $a.closest('li');
        if (!$li.hasClass('active')) {
            // Single traversal: find active <li>, remove .active from it and its <a>
            $listbox.find('li.active').removeClass('active').children('a').removeClass('active');
            $li.addClass('active');
            $a.addClass('active');
        }
    });

    $wrapper.attr(PATCHED_ATTR, 'true');
}

// ─── Init ─────────────────────────────────────────────────────────────────────

function initSelect($select, index) {
    hideEmptyValueOptions($select);
    $select.selectpicker('refresh');
    const $wrapper = $select.closest('.bootstrap-select');
    if ($wrapper.length && $wrapper.find('.dropdown-toggle').length) {
        patchSelect($select, index);
    }
}

function initAllSelects() {
    $(SELECTOR).each(function (i) {
        initSelect($(this), i);
    });
}

function patchUnpatched() {
    $(SELECTOR).each(function (i) {
        const $select = $(this);
        const $wrapper = $select.closest('.bootstrap-select');
        // Two cases require init:
        //   (a) no wrapper yet  → bootstrap-select not yet initialised (e.g. AJAX-injected select)
        //   (b) wrapper exists but not yet patched by us
        if (!$wrapper.length || $wrapper.attr(PATCHED_ATTR) !== 'true') {
            initSelect($select, i);
        }
    });
}

// ─── Bootstrap ────────────────────────────────────────────────────────────────

$(() => {
    $.fn.selectpicker.Constructor.BootstrapVersion = '4';
    $.fn.selectpicker.Constructor.DEFAULTS.title = null;
    // $.fn.selectpicker.Constructor.DEFAULTS.size = 10;

    ensureLiveRegion();
    initAllSelects();

    // Re-patch any selects that appear inside modals after they open.
    // Modals are the primary source of dynamically added selectpickers in SFCC SFRA.
    // Using shown.bs.modal is zero-overhead compared to a MutationObserver on body.
    // NOTE: this only works for modals whose content is already in the DOM when
    // shown.bs.modal fires. For modals that load content via AJAX (e.g. loginModal),
    // call patchUnpatched() explicitly after the AJAX response is injected.
    // Skip the full-page rescan entirely for modals with no selectpicker in their
    // own markup (e.g. search flyout) — avoids a page-wide querySelectorAll on every
    // modal open that could never have anything to patch.
    $(document).on('shown.bs.modal.a11y', function (e) {
        if (!$(e.target).find(SELECTOR).length) return;
        patchUnpatched();
    });
});

module.exports = { patchUnpatched };
